import { Logo } from '@/components/logo'
import { OnboardingLayout } from '@/components/onboarding-layout'
import { RequireAuth } from '@/components/require-auth'
import { UserService } from '@/services/user-service'
import { ApiError, Auth } from '@/services/auth-service'
import {
  useForm,
  Control,
  TextInput,
  Button,
  ObjectController,
} from '@tempots/beatui'
import { attr, html, on, Use } from '@tempots/dom'
import { Validation } from '@tempots/std'
import { HTMLTitle, Location } from '@tempots/ui'
import { z } from 'zod'
import { zColor } from '@/utils/zod'
import {
  AgencyMemberForm,
  AgencyMemberFormValues,
  agencyMemberSchema,
} from '@/components/agency-member'
import { PdfService } from '@/services/pdf-service'
import { PhoneNumberInput } from '@/components/phone-number-input'
import { LooseUrlInput } from '@/components/loose-url-input'
import { LinkedInInput } from '@/components/linkedin-input'
import { EphimeralService } from '@/utils/ephimeral'
import { OnboardingInfo } from '@/models/onboarding-info'
import { LooseEmailInput } from '@/components/loose-email-input'
import { Track } from '@/services/track'

const formSchema = z.object({
  agencyName: z.string().min(1).max(255),
  agencyPhoneNumber: z.string().min(1).max(255).or(z.undefined()),
  agencyUrl: z.string().or(z.undefined()),
  agencyLinkedIn: z.string().or(z.undefined()),
  agencyEmail: z.string().or(z.undefined()),
  agencyBrandColor: zColor,
  ...agencyMemberSchema.shape,
})

type OnboardingFormValues = z.infer<typeof formSchema>

export function OnboardingPage() {
  return RequireAuth({}, () =>
    Use(Location, location => {
      const authUser = Auth.user
      const onboardingInfo = EphimeralService.retrieve<OnboardingInfo>(
        'onboarding'
      ) ?? {
        type: 'onboarding',
      }
      const agencyName = onboardingInfo.headerAgencyName ?? ''
      const agencyPhoneNumber =
        onboardingInfo.headerAgencyPhoneNumber ?? undefined
      const agencyUrl = onboardingInfo.headerAgencyUrl ?? undefined
      const agencyLinkedIn = onboardingInfo.headerAgencyLinkedIn ?? undefined
      const agencyEmail = onboardingInfo.headerAgencyEmail ?? undefined
      const agencyBrandColor = onboardingInfo.agencyBrandColor ?? '#000000'
      const recruiterName =
        onboardingInfo.headerRecruiterName ?? authUser.value?.fullName ?? ''
      const recruiterEmail =
        onboardingInfo.headerRecruiterEmail ??
        authUser.value?.email ??
        undefined
      const recruiterPhoneNumber =
        onboardingInfo.headerRecruiterPhoneNumber ?? undefined
      const recruiterLinkedIn =
        onboardingInfo.headerRecruiterLinkedIn ?? undefined

      const { controller, submit, submitting } = useForm<OnboardingFormValues>({
        schema: formSchema,
        initialValue: {
          agencyName,
          agencyPhoneNumber,
          agencyUrl,
          agencyLinkedIn,
          agencyEmail,
          agencyBrandColor,
          recruiterName,
          recruiterEmail,
          recruiterPhoneNumber,
          recruiterLinkedIn,
        },
        onSubmit: async data => {
          const currentUser = authUser.value
          if (!currentUser) {
            return Validation.invalid({
              message: 'You need to be signed in to continue.',
            })
          }

          if ((data.agencyName?.trim() ?? '').length === 0) {
            Track.clientError({
              error_code: 'ONBOARDING_INVALID_AGENCY_NAME',
              error_message: 'Agency name is required',
            })
            return Validation.invalid({ message: 'Agency name is required.' })
          }

          if ((data.recruiterName?.trim() ?? '').length === 0) {
            Track.clientError({
              error_code: 'ONBOARDING_INVALID_RECRUITER_NAME',
              error_message: 'Recruiter name is required',
            })
            return Validation.invalid({ message: 'Your name is required.' })
          }

          Track.click({
            section: 'main',
            element_type: 'button',
            click_action: 'submit',
            element_id: 'signup',
          })

          try {
            // Cleared nullable inputs hold null; the API wants undefined.
            const response = await UserService.onboardToAgency({
              ...data,
              recruiterEmail: data.recruiterEmail ?? undefined,
              recruiterPhoneNumber: data.recruiterPhoneNumber ?? undefined,
              recruiterLinkedIn: data.recruiterLinkedIn ?? undefined,
            })
            // Consume ephemeral data after successful onboarding
            EphimeralService.consume('onboarding')
            // A paid pricing-card click carries through signup; those
            // users continue to checkout (with a skip path) instead of
            // the workspace. The intent stays stored until they
            // subscribe or dismiss the reminder.
            const planIntent = EphimeralService.retrieve<{
              type: 'planIntent'
              plan?: string
            }>('planIntent')
            location.navigate(
              planIntent?.plan
                ? `/${response.agencyId}/billing?intent=${planIntent.plan}`
                : `/${response.agencyId}/workspace`,
              { replace: true }
            )
            return Validation.valid
          } catch (error) {
            if (error instanceof ApiError) {
              if (
                error.status === 401 ||
                error.code === 'AUTHENTICATION_ERROR'
              ) {
                Auth.signout()
                location.navigate('/signin', { replace: true })
                Track.apiError({
                  error_code: 'ONBOARDING_SESSION_EXPIRED',
                  error_message: 'Session expired',
                })
                return Validation.invalid({
                  message: 'Your session expired. Please sign in again.',
                })
              }
              Track.apiError({
                error_code: error.code ?? 'ONBOARDING_FAILED',
                error_message: error.message ?? 'Failed to complete onboarding',
              })
            } else {
              Track.clientError({
                error_code: 'ONBOARDING_UNEXPECTED_ERROR',
                error_message:
                  error instanceof Error
                    ? error.message
                    : 'Failed to complete onboarding',
              })
            }

            console.error('Failed to complete onboarding', error)
            const message =
              error instanceof Error
                ? error.message
                : 'Failed to complete onboarding. Please try again.'
            return Validation.invalid({ message })
          }
        },
      })

      // When the user never picked a brand color (nothing carried over
      // from the trial page — the '#000000' initial above is just the
      // historical fallback), adopt the uploaded default design's authored
      // theme color once known, so the seeded agency looks as the admin
      // intended. The still-at-initial guard keeps a color the user picked
      // in the meantime from being overwritten.
      if (onboardingInfo.agencyBrandColor == null) {
        void PdfService.fetchTrialDefaultDesign().then(result => {
          if (!result || result.source !== 'uploaded') return
          const field = controller.field('agencyBrandColor')
          if (field.signal.value === '#000000') {
            field.change(result.defaultBrandColor)
          }
        })
      }

      return OnboardingLayout(
        {},
        HTMLTitle('Onboarding - ResumeHeaders'),
        html.form(
          on.submit(submit),
          attr.class('w-full md:max-w-160 p-4 text-sm md:text-md/5'),
          html.div(
            attr.class('flex justify-center mb-6 md:mb-8'),
            Logo({ size: 'main' })
          ),
          html.h2(
            attr.class('font-bold text-base md:text-lg mb-2'),
            'We need a little information about your agency to set you up.'
          ),
          html.h2(
            attr.class(
              'text-xs md:text-sm/5 font-bold mb-6 md:mb-8 text-gray-600'
            ),
            'This can be edited later.'
          ),
          html.div(
            attr.class('mb-8 flex flex-col gap-2'),
            Control(TextInput, {
              required: true,
              layout: 'horizontal-fixed',
              controller: controller.field('agencyName'),
              label: 'Agency Name',
              placeholder: 'Acme Corporation',
              onChange: v =>
                Track.fieldInput({
                  section: 'main',
                  field_name: 'agencyName',
                  previous_value: controller.field('agencyName').signal.value,
                  new_value: v,
                }),
            }),
            Control(PhoneNumberInput, {
              layout: 'horizontal-fixed',
              controller: controller.field('agencyPhoneNumber'),
              label: 'Agency Phone Number',
              onChange: v =>
                Track.fieldInput({
                  section: 'main',
                  field_name: 'agencyPhoneNumber',
                  previous_value:
                    controller.field('agencyPhoneNumber').signal.value ?? '',
                  new_value: v ?? '',
                }),
            }),
            Control(LooseUrlInput, {
              layout: 'horizontal-fixed',
              controller: controller.field('agencyUrl'),
              label: 'Agency Website',
              onChange: v =>
                Track.fieldInput({
                  section: 'main',
                  field_name: 'agencyUrl',
                  previous_value:
                    controller.field('agencyUrl').signal.value ?? '',
                  new_value: v ?? '',
                }),
            }),
            Control(LinkedInInput, {
              layout: 'horizontal-fixed',
              controller: controller.field('agencyLinkedIn'),
              label: 'Agency LinkedIn',
              onChange: v =>
                Track.fieldInput({
                  section: 'main',
                  field_name: 'agencyLinkedIn',
                  previous_value:
                    controller.field('agencyLinkedIn').signal.value ?? '',
                  new_value: v ?? '',
                }),
            }),
            Control(LooseEmailInput, {
              layout: 'horizontal-fixed',
              controller: controller.field('agencyEmail'),
              label: 'Agency Email',
              placeholder: 'info@example.com',
              onChange: v =>
                Track.fieldInput({
                  section: 'main',
                  field_name: 'agencyEmail',
                  previous_value:
                    controller.field('agencyEmail').signal.value ?? '',
                  new_value: v ?? '',
                }),
            })
            // agencyBrandColor stays in the form model (it seeds the
            // initial design and the wizard's unconfigured check compares
            // against it) but is no longer asked here — branding happens
            // in /customize.
          ),
          html.h2(
            attr.class('font-bold'),
            'Now We need a little information about you.'
          ),
          html.h2(
            attr.class('text-sm/5 font-bold'),
            'This can be edited later.'
          ),
          html.div(
            attr.class('mb-8 flex flex-col gap-2'),
            AgencyMemberForm({
              controller:
                controller as unknown as ObjectController<AgencyMemberFormValues>,
              onFieldChange: (fieldName, previousValue, newValue) =>
                Track.fieldInput({
                  section: 'main',
                  field_name: fieldName,
                  previous_value: previousValue,
                  new_value: newValue,
                }),
            })
          ),
          html.div(
            attr.class('flex justify-center'),
            Button(
              {
                loading: submitting,
                color: 'primary',
                size: 'lg',
                roundedness: 'md',
                type: 'submit',
              },
              'Continue'
            )
          )
        )
      )
    })
  )
}
